import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Typography, Stack, Box, Paper, Container } from "@mui/material";
import {
    useApplicationParameters,
    ApplicationParameterKeys,
    appParamCacheKey,
} from "src/api/client-api/appParams/useApplicationParameters";
import { useUpsertAppParam } from "src/api/client-api/appParams";
import { useQueryClient } from "react-query";
import { useFeatureActivation } from "src/api/client-api/menu/useFeatureActivation";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { ActionPlanModal } from "../AppParams/ExtraConfiguration/ActionPlanModal";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { FeatureDeactivationModal } from "./FeatureDeactivationModal";
import { EntityItem, FeatureItem } from "./feature.types";
import { MainEntitySection } from "./Entity/MainEntitySection";
import { GeneralFeature } from "./General/GeneralFeature";
import { URLHandler } from "src/services/URLHandler";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useSnackbar } from "src/utils/useSnackbar";

export default function FeatureActivation() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();
    const { applicationParameters } = useApplicationParameters();
    const upsertAppParam = useUpsertAppParam({ forceReload: false });
    const { activateFeature, deactivateFeature } = useFeatureActivation();
    const { isMobile } = useResponsiveBreakpoints();
    const [pendingToggle, setPendingToggle] = useState<{
        appParamKey: ApplicationParameterKeys;
        enabled: boolean;
        featureName: string;
    } | null>(null);

    const [isDeactivating, setIsDeactivating] = useState(false);
    const [actionPlanModalOpen, setActionPlanModalOpen] = useState(false);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [mutatingFeatures, setMutatingFeatures] = useState<Set<string>>(new Set());

    // Helper function to get boolean value from applicationParameters
    const getBooleanValue = useMemo(() => {
        return (appParamKey: ApplicationParameterKeys, defaultValue?: boolean) => {
            const _defaultValue = defaultValue ?? false;
            const value = applicationParameters?.[appParamKey];
            if (value === undefined) return _defaultValue;
            return value === "true";
        };
    }, [applicationParameters]);

    // Helper function to get current state - directly from server, no optimistic updates
    const getCurrentState = (appParamKey: ApplicationParameterKeys, defaultValue?: boolean) => {
        return getBooleanValue(appParamKey, defaultValue);
    };

    // Feature flags (FS-*) must use activate/deactivate — generic PUT/POST is rejected by the API.
    const usesFeatureToggleApi = (appParamKey: ApplicationParameterKeys): boolean => appParamKey.startsWith("FS-");

    // Helper function to perform the actual toggle (API call)
    const performToggle = (appParamKey: ApplicationParameterKeys, enabled: boolean) => {
        // Prevent multiple simultaneous mutations on the same feature
        if (mutatingFeatures.has(appParamKey)) {
            return;
        }

        // Mark this feature as mutating
        setMutatingFeatures((prev) => new Set(prev).add(appParamKey));

        if (usesFeatureToggleApi(appParamKey)) {
            const mutation = enabled ? activateFeature : deactivateFeature;
            mutation.mutate(appParamKey, {
                onSuccess: (data) => {
                    setMutatingFeatures((prev) => {
                        const next = new Set(prev);
                        next.delete(appParamKey);
                        return next;
                    });

                    const menuMessage = data.menus_updated ? ` (${data.menus_count || 0} menus mis à jour)` : "";

                    snackbar.setSnackbarProps({
                        type: "success",
                        message:
                            enabled ?
                                t("settings.feature.activated", "Feature activée avec succès") + menuMessage
                            :   t("settings.feature.deactivated", "Feature désactivée avec succès") + menuMessage,
                    });

                    setIsDeactivating(false);
                },
                onError: () => {
                    setMutatingFeatures((prev) => {
                        const next = new Set(prev);
                        next.delete(appParamKey);
                        return next;
                    });

                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t("settings.error", "Erreur lors de la sauvegarde de la feature"),
                    });
                    setIsDeactivating(false);
                },
            });

            return;
        }

        const wasValueOriginallySet = applicationParameters?.[appParamKey];
        upsertAppParam.mutate(
            {
                isNew: wasValueOriginallySet === undefined,
                key: appParamKey,
                value: String(enabled),
            },
            {
                onSuccess: () => {
                    setMutatingFeatures((prev) => {
                        const next = new Set(prev);
                        next.delete(appParamKey);
                        return next;
                    });

                    snackbar.setSnackbarProps({
                        type: "success",
                        message:
                            enabled ?
                                t("settings.feature.activated", "Feature activée avec succès")
                            :   t("settings.feature.deactivated", "Feature désactivée avec succès"),
                    });

                    queryClient.invalidateQueries([appParamCacheKey]);
                    setIsDeactivating(false);
                },
                onError: () => {
                    setMutatingFeatures((prev) => {
                        const next = new Set(prev);
                        next.delete(appParamKey);
                        return next;
                    });

                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t("settings.error", "Erreur lors de la sauvegarde de la feature"),
                    });
                    setIsDeactivating(false);
                },
            },
        );
    };

    // Helper function to create toggle handler with confirmation for deactivation
    const createToggleHandler = (appParamKey: ApplicationParameterKeys, featureName: string) => (enabled: boolean) => {
        const currentState = getCurrentState(appParamKey);

        // If we're disabling a currently enabled feature, show confirmation modal
        if (currentState && !enabled) {
            setPendingToggle({ appParamKey, enabled, featureName });
            setConfirmationModalOpen(true);
        } else {
            performToggle(appParamKey, enabled);
        }
    };

    // Handle confirmation modal actions
    const handleConfirmDeactivation = () => {
        if (pendingToggle) {
            setIsDeactivating(true);
            performToggle(pendingToggle.appParamKey, pendingToggle.enabled);
            setConfirmationModalOpen(false);
            setPendingToggle(null);
        }
    };

    const handleCancelDeactivation = () => {
        setConfirmationModalOpen(false);
        setPendingToggle(null);
        setIsDeactivating(false);
    };

    // General features section - mapped from AppParam.tsx
    const generalFeatures: FeatureItem[] = [
        {
            name: "CMS",
            description: "Content Management System",
            enabled: getCurrentState("FS-cms-enabled"),
            appParamKey: "FS-cms-enabled",
            onToggle: createToggleHandler("FS-cms-enabled", "CMS"),
            configureButton: {
                text: "Configurer",
                onClick: () => navigate("/settings/cms"),
            },
            navigationLink: "/settings/cms",
        },
        {
            name: "Page stratégie",
            description: "Feature switch of Stratégie",
            enabled: getCurrentState("FS-strategy"),
            appParamKey: "FS-strategy",
            onToggle: createToggleHandler("FS-strategy", "Page stratégie"),
            configureButton: {
                text: "Configurer",
                onClick: () => navigate("/settings/csrStrategy/general"),
            },
            navigationLink: "/settings/csrStrategy/general",
        },
        {
            name: "Documentary Space Page",
            description: "Feature switch to activate/deactivate the Documentary Space Page",
            enabled: getCurrentState("FS-documentary-space"),
            appParamKey: "FS-documentary-space",
            onToggle: createToggleHandler("FS-documentary-space", "Documentary Space Page"),
        },
        {
            name: "Gestion des ICVs",
            description:
                "Module de gestion des ICVs (Indicateurs de Cycle de Vie) permettant de créer, modifier et supprimer des ICVs",
            enabled: getCurrentState("FS-icv-management"),
            appParamKey: "FS-icv-management",
            onToggle: createToggleHandler("FS-icv-management", "Gestion des ICVs"),
            configureButton: {
                text: "Configurer",
                onClick: () => navigate("/settings/icvs"),
            },
            navigationLink: "/settings/icvs",
        },
        {
            name: t("sidebar.news_feed", "Newsfeed"),
            description: "Module de partage d'actualités et de communication interne avec posts, réactions et topics",
            enabled: getCurrentState("FS-news-feed"),
            appParamKey: "FS-news-feed",
            onToggle: createToggleHandler("FS-news-feed", "Actualités"),
            configureButton: {
                text: "Configurer",
                onClick: () => navigate("/settings/newsfeed"),
            },
            navigationLink: "/settings/newsfeed",
        },
        {
            name: "Intégrations applicatives",
            description: "Module de gestion des intégrations applicatives et webhooks",
            enabled: getCurrentState("FS-app-integration", false),
            appParamKey: "FS-app-integration",
            onToggle: createToggleHandler("FS-app-integration", "Intégrations applicatives"),
        },
        {
            name: "Company Settings",
            description: "Module of parameters used by the owner of the account, used to configure company information",
            enabled: getCurrentState("FS-company-settings", false),
            appParamKey: "FS-company-settings",
            onToggle: createToggleHandler("FS-company-settings", "Company Settings"),
        },
        {
            name: "Payment",
            description: "Module for managing payment and subscription information",
            enabled: getCurrentState("FS-payment-enabled", false),
            appParamKey: "FS-payment-enabled",
            onToggle: createToggleHandler("FS-payment-enabled", "Payment"),
        },
        {
            name: t("settings.consent.title", "Consent governance"),
            description:
                "Global switch for the consent gate, profile preference center, contractual acceptances, and admin governance screens.",
            enabled: getCurrentState(CONSENT_GOVERNANCE_FEATURE_SWITCH, false),
            appParamKey: CONSENT_GOVERNANCE_FEATURE_SWITCH,
            onToggle: createToggleHandler(CONSENT_GOVERNANCE_FEATURE_SWITCH, "Consent governance"),
            configureButton: {
                text: "Configurer",
                onClick: () => navigate(URLHandler.settings.consentGovernance()),
            },
            navigationLink: URLHandler.settings.consentGovernance(),
        },
        {
            name: "Export SSQ content as Excel",
            description: "Feature switch of export SSQ content as Excel module",
            enabled: getCurrentState("FS-ssq-export-excel"),
            appParamKey: "FS-ssq-export-excel",
            onToggle: createToggleHandler("FS-ssq-export-excel", "Export SSQ content as Excel"),
        },
        {
            name: t("sidebar.network", "Network"),
            description: t(
                "settings.feature.network_tracking.description",
                "Questionnaire progress and member activity of the network's organizations",
            ),
            enabled: getCurrentState("FS-network-tracking", false),
            appParamKey: "FS-network-tracking",
            onToggle: createToggleHandler("FS-network-tracking", t("sidebar.network", "Network")),
        },
    ];

    // Entities with features section
    const entitiesFeatures: EntityItem[] = [
        {
            name: t("sidebar.company_drawer", "Company"),
            description:
                "Module dédié à la gestion de l'entreprise.\nLes fonctionnalités « Scores & indicateurs clés » et « Questionnaire » sont activées par défaut.",
            icon: "corporate",
            enabled: getCurrentState("FS-entity-companies", true),
            onToggle: createToggleHandler("FS-entity-companies", "Entreprise (Global)"),
            features: [
                {
                    name: t("menu.company.diagnostic", "Diagnostic"),
                    description:
                        "Permet de collecter des données relatives à l'entreprise afin de calculer et analyser différentes scores et indicateurs.",
                    enabled: getCurrentState("FS-diagnostic" as ApplicationParameterKeys),
                    expandable: true,
                    hasSubFeatures: true,
                    // appParamKey: "FS-diagnostic" as ApplicationParameterKeys,
                    // onToggle: createToggleHandler("FS-diagnostic" as ApplicationParameterKeys, "Diagnostic"),
                    subFeatures: [
                        {
                            name: "Score Distribution",
                            description: "Feature switch of Distribution module",
                            enabled: getCurrentState("FS-company-score-details-distribution"),
                            appParamKey: "FS-company-score-details-distribution",
                            onToggle: createToggleHandler(
                                "FS-company-score-details-distribution",
                                "Score Distribution",
                            ),
                        },
                        {
                            name: "Score Comparison",
                            description: "Feature switch of Comparaison module",
                            enabled: getCurrentState("FS-company-score-details-comparison"),
                            appParamKey: "FS-company-score-details-comparison",
                            onToggle: createToggleHandler("FS-company-score-details-comparison", "Score Comparison"),
                        },
                        {
                            name: "Legal Requirements",
                            description: "Feature switch for the legal requirements section in company page",
                            enabled: getCurrentState("FS-legal-requirement"),
                            appParamKey: "FS-legal-requirement",
                            onToggle: createToggleHandler("FS-legal-requirement", "Legal Requirements"),
                        },
                    ],
                },
                {
                    name: t("sidebar.actionplan", "Action Plan & trajectories"),
                    description:
                        "Permet de définir une stratégie de progrès RSE à partir d'actions concrètes et de visualiser une trajectoire.",
                    enabled: getCurrentState("FS-actionPlan"),
                    appParamKey: "FS-actionPlan",
                    onToggle: createToggleHandler("FS-actionPlan", "Page Action Plan"),
                    configureButton: {
                        text: "Configurer",
                        onClick: () => setActionPlanModalOpen(true),
                    },
                },
            ],
        },
        {
            name: "Produits",
            description:
                "Module dédié à la création et à l'analyse des scores d'un produit rattachés à un QSS. Les rubriques « Scores & indicateurs clés » et « Questionnaire » sont activées par défaut.",
            icon: "products",
            enabled: getCurrentState("FS-entity-products", true),
            onToggle: createToggleHandler("FS-entity-products", "Produits (Global)"),
            features: [
                {
                    name: "Score Distribution",
                    description: "Feature switch of Distribution module",
                    enabled: getCurrentState("FS-product-score-details-distribution"),
                    appParamKey: "FS-product-score-details-distribution",
                    onToggle: createToggleHandler("FS-product-score-details-distribution", "Score Distribution"),
                },
                {
                    name: "Chaîne de valeur",
                    description: "Visualisation de la traçabilité amont/aval des produits et ingrédients",
                    enabled: getCurrentState("FS-product-value-chain"),
                    appParamKey: "FS-product-value-chain",
                    onToggle: createToggleHandler("FS-product-value-chain", "Chaîne de valeur"),
                },
                {
                    name: "Distribution produit",
                    description:
                        "Active l'étape de sélection du mode de distribution Ecobalyse dans le formulaire produit.",
                    enabled: getCurrentState("FS-product-distribution"),
                    appParamKey: "FS-product-distribution",
                    onToggle: createToggleHandler("FS-product-distribution", "Distribution produit"),
                    expandable: true,
                    hasSubFeatures: true,
                    subFeatures: [
                        {
                            name: "Manage my distribution",
                            description:
                                "Shows the Distribution module in the menu and enables Level 2 with platform distributions, scores, and SSQs.",
                            enabled: getCurrentState("FS-manage-my-distribution" as ApplicationParameterKeys),
                            appParamKey: "FS-manage-my-distribution" as ApplicationParameterKeys,
                            onToggle: createToggleHandler(
                                "FS-manage-my-distribution" as ApplicationParameterKeys,
                                "Manage my distribution",
                            ),
                        },
                    ],
                },
                {
                    name: "Consommation",
                    description: "Active la sélection des modes de consommation Ecobalyse dans le formulaire produit",
                    enabled: getCurrentState("FS-product-consumption"),
                    appParamKey: "FS-product-consumption",
                    onToggle: createToggleHandler("FS-product-consumption", "Consommation"),
                },
                {
                    name: "Transformation",
                    description:
                        "Enables Ecobalyse transformation process selection in the product form. Business process management is handled by the Manage my transformation option.",
                    enabled: getCurrentState("FS-product-transformation"),
                    appParamKey: "FS-product-transformation",
                    onToggle: createToggleHandler("FS-product-transformation", "Transformation"),
                    expandable: true,
                    hasSubFeatures: true,
                    subFeatures: [
                        {
                            name: "Manage my transformation",
                            description:
                                "Shows the Transformation module in the menu and enables Level 2 with platform processes, scores, and SSQs.",
                            enabled: getCurrentState("FS-manage-my-transformation" as ApplicationParameterKeys),
                            appParamKey: "FS-manage-my-transformation" as ApplicationParameterKeys,
                            onToggle: createToggleHandler(
                                "FS-manage-my-transformation" as ApplicationParameterKeys,
                                "Manage my transformation",
                            ),
                        },
                    ],
                },
                {
                    name: "Transport",
                    description:
                        "Active le transport Level 1 dans le formulaire produit avec des circuits de référence. Les étapes du cycle de vie sont configurables individuellement ci-dessous.",
                    enabled: getCurrentState("FS-product-transport"),
                    appParamKey: "FS-product-transport",
                    onToggle: createToggleHandler("FS-product-transport", "Transport produit"),
                    expandable: true,
                    hasSubFeatures: true,
                    subFeatures: [
                        {
                            name: "Manage my transport",
                            description:
                                "Affiche le module Transport dans le menu et active le Level 2 avec gestion des legs/circuits plateforme.",
                            enabled: getCurrentState("FS-manage-my-transport" as ApplicationParameterKeys),
                            appParamKey: "FS-manage-my-transport" as ApplicationParameterKeys,
                            onToggle: createToggleHandler(
                                "FS-manage-my-transport" as ApplicationParameterKeys,
                                "Manage my transport",
                            ),
                        },
                        {
                            name: "Étape « Produit → site de stockage »",
                            description:
                                "Étape Ecobalyse n°4 : trajet du produit fini vers le site de stockage. Toujours disponible quand le module Transport est actif.",
                            enabled: getCurrentState("FS-transport-stage-product-to-storage"),
                            appParamKey: "FS-transport-stage-product-to-storage",
                            onToggle: createToggleHandler(
                                "FS-transport-stage-product-to-storage",
                                "Transport — Produit → stockage",
                            ),
                        },
                        ...(getCurrentState("FS-entity-ingredients", true) ?
                            [
                                {
                                    name: "Étape « Ingrédient → site de transformation »",
                                    description:
                                        "Étape Ecobalyse n°1 : trajet local de l'ingrédient vers le site de transformation. Disponible si le module Ingrédients est actif.",
                                    enabled: getCurrentState(
                                        "FS-transport-stage-ingredient-to-processing-site" as ApplicationParameterKeys,
                                    ),
                                    appParamKey:
                                        "FS-transport-stage-ingredient-to-processing-site" as ApplicationParameterKeys,
                                    onToggle: createToggleHandler(
                                        "FS-transport-stage-ingredient-to-processing-site" as ApplicationParameterKeys,
                                        "Transport — Ingrédient → transformation",
                                    ),
                                },
                            ]
                        :   []),
                        ...(getCurrentState("FS-entity-packagings", true) ?
                            [
                                {
                                    name: "Étape « Emballage → site de transformation »",
                                    description:
                                        "Trajet de l'emballage vers le site de conditionnement. Disponible si le module Emballage est actif.",
                                    enabled: getCurrentState(
                                        "FS-transport-stage-packaging-to-processing-site" as ApplicationParameterKeys,
                                    ),
                                    appParamKey:
                                        "FS-transport-stage-packaging-to-processing-site" as ApplicationParameterKeys,
                                    onToggle: createToggleHandler(
                                        "FS-transport-stage-packaging-to-processing-site" as ApplicationParameterKeys,
                                        "Transport — Emballage → transformation",
                                    ),
                                },
                            ]
                        :   []),
                        ...(getCurrentState("FS-product-distribution") ?
                            [
                                {
                                    name: "Étape « Site de stockage → vente »",
                                    description:
                                        "Étape Ecobalyse n°5 : trajet du site de stockage vers le point de vente. Disponible si la Distribution produit est active.",
                                    enabled: getCurrentState(
                                        "FS-transport-stage-storage-to-retail" as ApplicationParameterKeys,
                                    ),
                                    appParamKey: "FS-transport-stage-storage-to-retail" as ApplicationParameterKeys,
                                    onToggle: createToggleHandler(
                                        "FS-transport-stage-storage-to-retail" as ApplicationParameterKeys,
                                        "Transport — Stockage → vente",
                                    ),
                                },
                            ]
                        :   []),
                        ...(getCurrentState("FS-product-consumption") ?
                            [
                                {
                                    name: "Étape « Vente → consommateur »",
                                    description:
                                        "Trajet du point de vente vers le consommateur final. Disponible si la Consommation est active.",
                                    enabled: getCurrentState(
                                        "FS-transport-stage-retail-to-consumer" as ApplicationParameterKeys,
                                    ),
                                    appParamKey: "FS-transport-stage-retail-to-consumer" as ApplicationParameterKeys,
                                    onToggle: createToggleHandler(
                                        "FS-transport-stage-retail-to-consumer" as ApplicationParameterKeys,
                                        "Transport — Vente → consommateur",
                                    ),
                                },
                            ]
                        :   []),
                    ],
                },
                {
                    name: "Version UX Produit",
                    description:
                        "v1 = interface historique. v2 = nouvelle présentation des écrans couverts pour les produits finis et intermédiaires, les ingrédients, les emballages, les fournisseurs, les transformations et les transports. Le formulaire produit adopte une page en défilement de 1 à 9 sections, affichées dynamiquement selon les attributs configurés, les structures disponibles, les modules actifs et les droits de l'utilisateur. Le nouvel habillage V2 de la vue détaillée d'un score est réservé aux produits finis.",
                    enabled: getCurrentState("FS-product-ux-v2"),
                    appParamKey: "FS-product-ux-v2",
                    control: "v1v2",
                    onToggle: createToggleHandler("FS-product-ux-v2", "Version UX Produit"),
                },
                {
                    name: "Recherche Open Food Facts",
                    description:
                        "Active la recherche Open Food Facts dans la fiche produit V2 pour pré-remplir le produit, son EAN, sa référence Agribalyse, ses ingrédients et sa photo.",
                    enabled: getCurrentState("FS-open-food-facts-search"),
                    appParamKey: "FS-open-food-facts-search",
                    onToggle: createToggleHandler("FS-open-food-facts-search", "Recherche Open Food Facts"),
                },
            ],
        },
        {
            name: "Produits Intermédiaires",
            description:
                "Module dédié aux produits intermédiaires utilisables comme ingrédients. Les produits intermédiaires permettent de créer des ingrédients miroirs qui peuvent être utilisés dans la composition d'autres produits.",
            icon: "products",
            enabled: getCurrentState("FS-entity-intermediate-products", false),
            onToggle: createToggleHandler("FS-entity-intermediate-products", "Produits Intermédiaires"),
            features: [],
        },
        {
            name: "Ingrédients",
            description:
                "Active l'utilisation des ingrédients de référence dans les produits. La gestion des ingrédients métier est portée par l'option Manage my ingredients.",
            icon: "ingredients",
            enabled: getCurrentState("FS-entity-ingredients", true),
            onToggle: createToggleHandler("FS-entity-ingredients", "Ingredients (Global)"),
            features: [
                {
                    name: "Manage my ingredients",
                    description:
                        "Affiche le module Ingrédients dans le menu et active le Level 2 avec ingrédients plateforme, scores et SSQ.",
                    enabled: getCurrentState("FS-manage-my-ingredients" as ApplicationParameterKeys),
                    appParamKey: "FS-manage-my-ingredients" as ApplicationParameterKeys,
                    onToggle: createToggleHandler(
                        "FS-manage-my-ingredients" as ApplicationParameterKeys,
                        "Manage my ingredients",
                    ),
                },
                {
                    name: "Score Distribution",
                    description: "Feature switch of Distribution module",
                    enabled: getCurrentState("FS-ingredient-score-details-distribution"),
                    appParamKey: "FS-ingredient-score-details-distribution",
                    onToggle: createToggleHandler("FS-ingredient-score-details-distribution", "Score Distribution"),
                },
                {
                    name: "Référentiel ingrédients plateforme",
                    description:
                        "Permet aux administrateurs de créer leurs propres ingrédients de référence avec des facteurs d'émission spécifiques, utilisables comme alternative au catalogue Sure",
                    enabled: getCurrentState("FS-platform-ingredient-references"),
                    appParamKey: "FS-platform-ingredient-references",
                    onToggle: createToggleHandler(
                        "FS-platform-ingredient-references",
                        "Référentiel ingrédients plateforme",
                    ),
                    configureButton: {
                        text: "Configurer",
                        onClick: () => navigate("/settings/ingredient-references"),
                    },
                    navigationLink: "/settings/ingredient-references",
                },
            ],
        },
        {
            name: "Emballage",
            description:
                "L'activation de l'entité Emballage permet de faire apparaître un module dédié à la création et à l'analyse des scores d'un emballage rattachés à des références de packaging.",
            icon: "packaging",
            enabled: getCurrentState("FS-entity-packagings", true),
            onToggle: createToggleHandler("FS-entity-packagings", "Packagings (Global)"),
            features: [
                {
                    name: "Manage my packagings",
                    description:
                        "Permet d'afficher l'entrée de gestion des emballages dans le menu et de sélectionner les emballages de la plateforme dans les produits.",
                    enabled: getCurrentState("FS-manage-my-packagings"),
                    appParamKey: "FS-manage-my-packagings",
                    onToggle: createToggleHandler("FS-manage-my-packagings", "Manage my packagings"),
                },
                {
                    name: "Score Distribution",
                    description: "Feature switch of Distribution module",
                    enabled: getCurrentState("FS-packaging-score-details-distribution"),
                    appParamKey: "FS-packaging-score-details-distribution",
                    onToggle: createToggleHandler("FS-packaging-score-details-distribution", "Score Distribution"),
                },
            ],
        },
    ];

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: isMobile ? "0.5rem" : "48px !important",
                flexGrow: 1,
                overflow: "auto",
            }}
        >
            <SettingsPageHeader
                title={t("settings.platform.platform_setup.modules.title", "Modules")}
                subtitle={t(
                    "settings.platform.platform_setup.modules.description",
                    "Activate or deactivate platform capabilities.",
                )}
                breadcrumbs={[
                    {
                        label: t("settings.platform.title", "Platform"),
                        to: URLHandler.settings.configuration(),
                    },
                    {
                        label: t("settings.platform.platform_setup.title", "Platform setup"),
                        to: URLHandler.settings.configurationPath("platform-setup"),
                    },
                    { label: t("settings.platform.platform_setup.modules.title", "Modules") },
                ]}
            />

            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    border: "1px solid #E7E6E4",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "white",
                    boxShadow: "none",
                }}
            >
                <Box sx={{ padding: isMobile ? "0.5rem" : "16px 40px 40px 40px", backgroundColor: "#FEFCFB" }}>
                    <Stack spacing={4}>
                        {/* General Features Section */}
                        <Stack spacing={3}>
                            <Typography
                                variant="h5"
                                sx={{
                                    lineHeight: "1.6em",
                                    color: "#393A36",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Général
                            </Typography>

                            {generalFeatures.map((feature, index) => (
                                <GeneralFeature
                                    key={feature.appParamKey ?? feature.name}
                                    feature={feature}
                                    index={index}
                                />
                            ))}
                        </Stack>

                        {/* Entities Section */}
                        <Stack spacing={3}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: "#393A36",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Entités
                            </Typography>

                            {entitiesFeatures.map((entity) => (
                                <Box key={entity.name}>
                                    <MainEntitySection entity={entity} />
                                </Box>
                            ))}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <FeatureDeactivationModal
                open={confirmationModalOpen}
                onClose={handleCancelDeactivation}
                onConfirm={handleConfirmDeactivation}
                isDeactivating={isDeactivating}
                featureName={pendingToggle?.featureName || ""}
            />

            <ActionPlanModal isOpen={actionPlanModalOpen} setOpen={setActionPlanModalOpen} />
        </Container>
    );
}
